import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, EyeOff } from "lucide-react";
import { LOGO_URL, LOGO_MARK_URL } from "@/lib/api";
import { useSiteSettings } from "@/components/SiteGate";

// Only shown to signed-in admins while the public site is behind the Coming Soon page
const HiddenBadge = () => {
  const { settings } = useSiteSettings();
  if (!settings || settings.site_public) return null;
  return (
    <Link to="/admin" title="Visitors currently see a Coming Soon page. Change in Admin → Integrations." className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30 hover:bg-amber-400/25 transition-colors" data-testid="site-hidden-badge">
      <EyeOff size={12} /> Hidden
    </Link>
  );
};

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/map", label: "Interactive Map" },
  { to: "/research", label: "Research & Articles" },
  { to: "/videos", label: "Video Media" },
  { to: "/connect", label: "Connect" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  return (
    <header className="sticky top-0 z-[1000] bg-black text-white border-b border-white/10">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
          <div className="w-12 h-12 rounded-md overflow-hidden flex items-center justify-center shrink-0">
            <img src={LOGO_MARK_URL} alt="DCEPtNZ" className="w-full h-full object-cover scale-[1.9]" />
          </div>
          <div className="leading-none">
            <div className="font-mono text-[13px] font-semibold tracking-[0.18em] text-white">DCEPtNZ</div>
            <div className="text-[10px] text-white/60 tracking-wide mt-0.5 whitespace-nowrap" data-testid="header-tagline">Data Centre Exposure Project NZ</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors dcep-link-underline ${isActive ? "text-emerald-400" : "text-white/70 hover:text-white"}`}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {n.label}
            </NavLink>
          ))}
          <Link to="/login" className="text-sm font-medium px-4 py-2 rounded-full bg-white text-black hover:bg-emerald-400 transition-colors" data-testid="nav-admin">
            Admin Portal
          </Link>
          <HiddenBadge />
        </nav>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black px-5 py-4 flex flex-col gap-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              className={({ isActive }) => `text-base font-medium py-1 ${isActive ? "text-emerald-400" : "text-white"}`}>
              {n.label}
            </NavLink>
          ))}
          <Link to="/login" onClick={() => setOpen(false)} className="mt-2 text-center text-sm font-medium px-4 py-2.5 rounded-full bg-white text-black">
            Admin Portal
          </Link>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-[#0B0F17] text-white/70 dcep-grain border-t-2 border-emerald-500/30 mt-24">
    <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-14 grid md:grid-cols-3 gap-10">
      <div>
        <div className="flex items-center gap-4 mb-5">
          <img src={LOGO_URL} alt="DCEPtNZ" className="w-32 h-32 rounded-lg" />
          <div>
            <div className="font-mono text-sm font-semibold tracking-[0.18em] text-white">DCEPtNZ</div>
            <div className="text-[11px] text-white/50">Data Centre Exposure Project NZ</div>
          </div>
        </div>
        <p className="text-sm leading-relaxed text-white/50 max-w-xs">
          An independent research project tracking how data centres, energy systems and international connections are reshaping New Zealand.
        </p>
      </div>
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-400 mb-4">Navigate</div>
        <ul className="space-y-2.5 text-sm">
          <li><Link to="/map" className="hover:text-white">Interactive Map</Link></li>
          <li><Link to="/research" className="hover:text-white">Research & Articles</Link></li>
          <li><Link to="/videos" className="hover:text-white">Video Media</Link></li>
          <li><Link to="/connect" className="hover:text-white">Connect</Link></li>
        </ul>
      </div>
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-400 mb-4">About this record</div>
        <p className="text-sm leading-relaxed text-white/50">
          This archive is built in public and is non-commercial. Documents and sources are cited where available.
        </p>
        <p className="text-xs text-white/35 mt-5">© {new Date().getFullYear()} DCEPtNZ. Independent research.</p>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
